import { useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Button, Card, Callout, Spinner, Badge, Select, Table, type Column } from "@/design/components";
import { ApiError } from "@/api/client";
import { useAuth } from "@/app/auth";
import { listAudit, type AuditEntry } from "@/api/audit";
import { getStats, type ActivityBucket } from "@/api/stats";
import { listMembers } from "@/api/members";
import { downloadCsv } from "@/lib/csv";
import styles from "./AuditScreen.module.css";

const AUDIT_PAGE_SIZE = 50;

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

// Humanized labels for the canonical audit actions (api AUDIT_ACTIONS). An unknown
// action falls back to its raw string so a newly-added action type is never hidden.
const ACTION_LABEL: Record<string, string> = {
  "org.provision": "Organization created",
  "channel.create": "Channel created",
  "channel.delete": "Channel disconnected",
  "member.invite": "Member invited",
  "member.remove": "Member removed",
  "member.role_changed": "Member role changed",
  "org.rotate": "Org_ID rotated",
  "org.tier_changed": "Plan changed",
  "channel.secret_rotated": "Channel secret rotated",
  "org.renamed": "Organization renamed",
  "org.tos_accepted": "Terms accepted",
};
const actionLabel = (a: string): string => ACTION_LABEL[a] ?? a;

// Server-side filter options — the known action types (the filter is applied at the
// API now, so we can't derive options from a single client page).
const AUDIT_FILTER_OPTIONS = Object.keys(ACTION_LABEL).map((a) => ({ value: a, label: actionLabel(a) }));

type Tone = "accent" | "neutral" | "success" | "warning" | "danger";
const ACTION_TONE: Record<string, Tone> = {
  "org.provision": "accent",
  "channel.create": "success",
  "channel.delete": "warning",
  "member.invite": "success",
  "member.remove": "warning",
  "member.role_changed": "accent",
  "org.rotate": "warning",
  "org.tier_changed": "accent",
  "channel.secret_rotated": "warning",
  "org.renamed": "neutral",
  "org.tos_accepted": "neutral",
};
const actionTone = (a: string): Tone => ACTION_TONE[a] ?? "neutral";

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Bucket dates are UTC "YYYY-MM-DD"; render in UTC so the label matches the bucket.
function shortDay(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? isoDate
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Compact "key: value" summary of an audit row's non-secret detail object. */
function detailSummary(detail: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(detail)) {
    if (v === null || v === undefined || typeof v === "object") continue;
    parts.push(`${k}: ${String(v)}`);
  }
  return parts.join(" · ");
}

/**
 * Activity (Phase 6 / FR32) — the org's compliance + insight surface. Two parts:
 * an Overview (lifetime totals + an administrative-activity chart from
 * /admin/stats) and the Audit log (the write-once trail from /admin/audit, with a
 * client-side action filter). Actor ids are enriched to member names via the
 * /admin/members data (best-effort — a raw UUID is meaningless), falling back to a
 * short id when the actor isn't a current member (e.g. removed since).
 *
 * The chart is ADMINISTRATIVE activity (channel/member/tier/Org_ID changes), NOT
 * message/traffic volume — labeled as such so it never overstates what it shows.
 */
export function AuditScreen() {
  const { admin } = useAuth();
  const meId = admin?.id;

  // Server-side action filter drives the query key (a change re-fetches from page 1).
  const [actionFilter, setActionFilter] = useState<string>("all");

  const statsQuery = useQuery({ queryKey: ["stats"], queryFn: ({ signal }) => getStats(undefined, signal) });
  const auditQuery = useInfiniteQuery({
    queryKey: ["audit", actionFilter],
    queryFn: ({ pageParam, signal }) =>
      listAudit(
        { limit: AUDIT_PAGE_SIZE, action: actionFilter === "all" ? undefined : actionFilter, before: pageParam },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Keep the current rows visible while a filter change / next page loads (stable
    // table, no flash to empty).
    placeholderData: keepPreviousData,
  });
  // Enrichment only — a failure here just means actors show as short ids (no error UI).
  const membersQuery = useQuery({ queryKey: ["members"], queryFn: ({ signal }) => listMembers(signal) });

  const memberLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) map.set(m.id, m.name?.trim() || m.email);
    return map;
  }, [membersQuery.data]);

  function actorLabel(entry: AuditEntry): { text: string; mono: boolean } {
    if (entry.actorAdminId === null) return { text: "System", mono: false };
    if (entry.actorAdminId === meId) return { text: "You", mono: false };
    const named = memberLabel.get(entry.actorAdminId);
    if (named) return { text: named, mono: false };
    return { text: `${entry.actorAdminId.slice(0, 8)}…`, mono: true };
  }

  // All loaded pages flattened (the server already filtered + ordered newest-first).
  const entries = useMemo(() => auditQuery.data?.pages.flatMap((p) => p.entries) ?? [], [auditQuery.data]);
  const hasLoaded = auditQuery.data !== undefined;
  const showFilter = hasLoaded && !auditQuery.isError && (entries.length > 0 || actionFilter !== "all");

  const columns: Column<AuditEntry>[] = [
    { key: "when", header: "When", render: (e) => <span className={styles.when}>{formatDateTime(e.createdAt)}</span> },
    { key: "action", header: "Action", render: (e) => <Badge tone={actionTone(e.action)}>{actionLabel(e.action)}</Badge> },
    {
      key: "by",
      header: "By",
      render: (e) => {
        const a = actorLabel(e);
        return <span className={a.mono ? styles.mono : undefined}>{a.text}</span>;
      },
    },
    { key: "detail", header: "Details", render: (e) => <span className={styles.detail}>{detailSummary(e.detail) || "—"}</span> },
  ];

  function exportCsv(): void {
    downloadCsv(
      `cogent-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      ["When", "Action", "By", "Details"],
      entries.map((e) => [formatDateTime(e.createdAt), actionLabel(e.action), actorLabel(e).text, detailSummary(e.detail)]),
    );
  }

  // Activity stats CSV — one row per (day, action) so the per-day breakdown is preserved
  // (long format avoids a sparse, variable column-per-action matrix).
  function exportActivityCsv(): void {
    const s = statsQuery.data;
    if (!s) return;
    const rows: string[][] = [];
    for (const b of s.activity) {
      for (const [action, count] of Object.entries(b.byAction)) {
        rows.push([b.date, actionLabel(action), String(count)]);
      }
    }
    downloadCsv(`cogent-activity-${new Date().toISOString().slice(0, 10)}.csv`, ["Date", "Action", "Count"], rows);
  }

  const stats = statsQuery.data;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Activity</h1>
        </div>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <div className={styles.logHead}>
          <h2 className={styles.sectionTitle}>Overview</h2>
          {stats && stats.activity.length > 0 && (
            <Button variant="secondary" size="sm" onClick={exportActivityCsv}>
              Export activity CSV
            </Button>
          )}
        </div>

        {statsQuery.isPending && (
          <div className={styles.loading}>
            <Spinner size={20} color="var(--accent)" /> <span>Loading activity…</span>
          </div>
        )}
        {statsQuery.isError && (
          <Callout tone="danger" title="Couldn't load activity">
            {apiMessage(statsQuery.error, "Please try again shortly.")}
          </Callout>
        )}
        {stats && (
          <>
            <div className={styles.tiles}>
              <StatTile label="Channels" value={stats.totals.channels} />
              <StatTile label="Team members" value={stats.totals.members} />
              <StatTile label={`Audited actions · ${stats.rangeDays}d`} value={stats.totals.events} />
            </div>
            <ActivityChart activity={stats.activity} rangeDays={stats.rangeDays} />
          </>
        )}
      </section>

      {/* Audit log */}
      <section className={styles.section}>
        <div className={styles.logHead}>
          <h2 className={styles.sectionTitle}>Audit log</h2>
          <div className={styles.logActions}>
            {showFilter && (
              <div className={styles.filter}>
                <Select
                  label="Filter by action"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  options={[{ value: "all", label: "All actions" }, ...AUDIT_FILTER_OPTIONS]}
                />
              </div>
            )}
            {entries.length > 0 && (
              <Button variant="secondary" size="sm" onClick={exportCsv}>
                Export CSV
              </Button>
            )}
          </div>
        </div>

        {auditQuery.isPending && (
          <div className={styles.loading}>
            <Spinner size={20} color="var(--accent)" /> <span>Loading audit log…</span>
          </div>
        )}
        {auditQuery.isError && (
          <Callout tone="danger" title="Couldn't load the audit log">
            {apiMessage(auditQuery.error, "Please try again shortly.")}
          </Callout>
        )}
        {hasLoaded && !auditQuery.isError && (
          <Card className={styles.tableCard}>
            <Table
              columns={columns}
              rows={entries}
              getRowKey={(e) => e.id}
              emptyText={
                actionFilter === "all"
                  ? "No activity recorded yet. Administrative actions — creating channels, inviting members, changing your plan — appear here as a tamper-evident, write-once trail."
                  : "No entries match this filter."
              }
            />
          </Card>
        )}
        {auditQuery.hasNextPage && (
          <div className={styles.loadMore}>
            <Button
              variant="secondary"
              onClick={() => void auditQuery.fetchNextPage()}
              loading={auditQuery.isFetchingNextPage}
            >
              Load more
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <Card className={styles.tile}>
      <span className={styles.tileValue}>{value}</span>
      <span className={styles.tileLabel}>{label}</span>
    </Card>
  );
}

function ActivityChart({ activity, rangeDays }: { activity: ActivityBucket[]; rangeDays: number }) {
  if (activity.length === 0) {
    return (
      <Card className={styles.chartEmpty}>
        <p className={styles.emptyBlurb}>No administrative activity in the last {rangeDays} days.</p>
      </Card>
    );
  }
  const max = Math.max(1, ...activity.map((b) => b.total));
  return (
    <Card className={styles.chartCard}>
      <figure className={styles.chart}>
        <figcaption className={styles.chartCaption}>Administrative activity — last {rangeDays} days</figcaption>
        <div className={styles.bars} role="list">
          {activity.map((b) => {
            const breakdown = Object.entries(b.byAction)
              .map(([a, n]) => `${actionLabel(a)}: ${n}`)
              .join(", ");
            const label = `${b.date}: ${b.total} action${b.total === 1 ? "" : "s"} (${breakdown})`;
            return (
              <div key={b.date} className={styles.barCol} role="listitem" title={label} aria-label={label}>
                <div className={styles.barTrack}>
                  <div className={styles.bar} style={{ height: `${Math.round((b.total / max) * 100)}%` }} />
                </div>
                <span className={styles.barDay}>{shortDay(b.date)}</span>
              </div>
            );
          })}
        </div>
      </figure>
    </Card>
  );
}
